import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Info as InfoIcon,
  AddCircleOutline as AddIcon,
  CreateNewFolder as CreateNewFolderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import { hasUserTagVariableReservedChars } from '../Utils/tagUtils';

// Theme colors to match Sidebar
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';

const ManualCreationDialog = ({ open, onClose, onCreateProject }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagErrors, setTagErrors] = useState({});

  const handleClose = () => {
    // Reset form
    setTitle('');
    setDescription('');
    setTags([]);
    setTagErrors({});
    onClose();
  };

  const validateTags = () => {
    const nextErrors = {};
    tags.forEach((field, index) => {
      const variable = String(field.variable || '').trim();
      const descriptionValue = String(field.description || '').trim();
      if (!variable) {
        nextErrors[`variable-${index}`] = t('settings.variable_required');
      } else if (hasUserTagVariableReservedChars(variable)) {
        nextErrors[`variable-${index}`] = t('settings.variable_no_space_underscore');
      }
      if (!descriptionValue) {
        nextErrors[`description-${index}`] = t('settings.description_required');
      }
    });
    setTagErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreate = () => {
    if (!validateTags()) {
      return;
    }
    const created = onCreateProject({
      title,
      description,
      customFields: tags.map(field => ({
        variable: String(field.variable || '').trim(),
        description: String(field.description || '').trim()
      }))
    });
    if (created) {
      handleClose();
    }
  };

  const handleCustomFieldChange = (index, field, value) => {
    const updatedFields = [...tags];
    updatedFields[index][field] = value;
    setTags(updatedFields);
    setTagErrors(prev => {
      const next = { ...prev };
      delete next[`${field}-${index}`];
      return next;
    });
  };

  const addCustomField = () => {
    setTags([...tags, { variable: '', description: '' }]);
    setTagErrors({});
  };

  const removeCustomField = (index) => {
    const updatedFields = tags.filter((_, i) => i !== index);
    setTags(updatedFields);
    setTagErrors({});
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #fcfcff 100%)',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon
            sx={{
              color: primaryColor,
              fontSize: 28,
              mr: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: primaryColor,
              fontWeight: 'bold'
            }}
          >
            {t('addcase.manual_creation_button')}
          </Typography>
        </Box>
        <CloseButton
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        />
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 3,
            fontStyle: 'italic'
          }}
        >
          {t(
            'addcase.manual_details_subtitle',
            'Manual mode creates a blank project. AI will not auto-generate widgets, project understanding, or tags.'
          )}
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(87, 87, 176, 0.08)',
            border: '1px solid rgba(87, 87, 176, 0.22)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t(
              'addcase.manual_mode_simple_notice',
              'Manual mode = blank project only. Widgets, project understanding, and tags are not auto-generated by AI.'
            )}
          </Typography>
        </Box>

        {/* Title and Description Fields */}
        <TextField
          label={t('addcase.title')}
          variant="outlined"
          fullWidth
          margin="normal"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover fieldset': {
                borderColor: hoverColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
              }
            },
            '& .MuiInputLabel-outlined.Mui-focused': {
              color: primaryColor,
            }
          }}
          InputProps={{
            sx: {
              fontSize: '16px',
              fontWeight: 500
            }
          }}
        />

        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('addcase.tooltip_why_important')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('addcase.tooltip_more_context')}
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                <Box component="li">{t('hardcoded.project_objectives')}</Box>
                <Box component="li">{t('hardcoded.document_types')}</Box>
                <Box component="li">{t('hardcoded.ai_focus_areas')}</Box>
                <Box component="li">{t('hardcoded.background_information')}</Box>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('addcase.tooltip_context_conclusion')}
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <TextField
            label={t('addcase.description')}
            variant="outlined"
            fullWidth
            multiline
            rows={5}
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('addcase.description_placeholder')}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover fieldset': {
                  borderColor: hoverColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                }
              },
              '& .MuiInputLabel-outlined.Mui-focused': {
                color: primaryColor,
              }
            }}
            InputProps={{
              endAdornment: (
                <InfoIcon
                  color="action"
                  sx={{
                    opacity: 0.6,
                    '&:hover': {
                      opacity: 1,
                      color: primaryColor
                    }
                  }}
                />
              )
            }}
          />
        </Tooltip>

        {/* Custom Tags Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AddIcon
              sx={{
                color: primaryColor,
                fontSize: 28,
                mr: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: primaryColor,
                fontWeight: 'bold'
              }}
            >
              {t('addcase.custom_tags')}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 4,
              fontStyle: 'italic',
              pl: 5
            }}
          >
            {t('addcase.define_tags')} - {t('addcase.custom_tags_subtitle')}
          </Typography>

          {tags.map((field, index) => (
            <Box
              key={index}
              className="custom-field"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 3,
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 3px 8px rgba(87, 87, 176, 0.08)',
                border: '1px solid rgba(87, 87, 176, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  borderColor: 'rgba(87, 87, 176, 0.2)',
                }
              }}
            >
              <TextField
                label={t('addcase.tag_name')}
                variant="outlined"
                margin="normal"
                value={field.variable}
                onChange={(e) => handleCustomFieldChange(index, 'variable', e.target.value)}
                className="variable"
                size="small"
                error={!!tagErrors[`variable-${index}`]}
                helperText={tagErrors[`variable-${index}`] || ''}
                sx={{
                  mr: 2,
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '&:hover fieldset': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: primaryColor,
                    }
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  }
                }}
              />

              <TextField
                label={t('addcase.describe_tag')}
                variant="outlined"
                margin="normal"
                value={field.description}
                onChange={(e) => handleCustomFieldChange(index, 'description', e.target.value)}
                className="description"
                size="small"
                error={!!tagErrors[`description-${index}`]}
                helperText={tagErrors[`description-${index}`] || ''}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    '&:hover fieldset': {
                      borderColor: hoverColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: primaryColor,
                    }
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  }
                }}
              />

              <CloseButton
                onClick={() => removeCustomField(index)}
                ariaLabel="remove"
                size="small"
                className="remove-button"
                sx={{
                  backgroundColor: '#ff6666',
                  '&:hover': {
                    backgroundColor: '#ff4444',
                    transform: 'scale(1.05)',
                  },
                  mt: 1.5,
                  ml: 1,
                  p: '6px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(255,102,102,0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#fff',
                  transition: 'all 0.2s ease',
                }}
              />
            </Box>
          ))}

          {tags.length === 0 && (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '12px',
                backgroundColor: 'rgba(240, 240, 255, 0.5)',
                border: '1px dashed rgba(87, 87, 176, 0.3)',
                mb: 3
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                {t('addcase.no_custom_tags')}
              </Typography>
            </Box>
          )}

          <Box
            className="add-button"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3
            }}
          >
            <Button
              onClick={addCustomField}
              variant="contained"
              startIcon={<AddIcon />}
              aria-label="add tag"
              sx={{
                backgroundColor: primaryColor,
                '&:hover': {
                  backgroundColor: hoverColor,
                  transform: 'translateY(-2px)',
                },
                px: 3,
                py: 1,
                borderRadius: '12px',
                boxShadow: '0 3px 10px rgba(87, 87, 176, 0.3)',
                color: '#fff',
                transition: 'all 0.2s ease',
                fontWeight: 'medium'
              }}
            >
              {t('addcase.add_custom_tag')}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          variant="contained"
          startIcon={<CreateNewFolderIcon />}
          onClick={handleCreate}
          sx={{
            px: 5,
            py: 1.5,
            fontSize: '1rem',
            borderRadius: '50px',
            boxShadow: '0 4px 14px rgba(87, 87, 176, 0.25)',
            fontWeight: 600,
            background: `linear-gradient(to right, ${primaryColor}, ${hoverColor})`,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(87, 87, 176, 0.35)',
              background: `linear-gradient(to right, ${primaryColor}, ${selectedColor})`,
            }
          }}
        >
          {t('addcase.create_analysis')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualCreationDialog;
